import type { RGBA, Gradient, ParsedColor, ColorRef } from '../types';

/**
 * Map of friendly color names to global style color references
 */
const COLOR_NAME_MAP: Record<string, ColorRef> = {
  primary: '$ref:#/colors/color1',
  secondary: '$ref:#/colors/color2',
  tertiary: '$ref:#/colors/color3',
  text: '$ref:#/colors/color4',
  accent: '$ref:#/colors/color5',
  background: '$ref:#/colors/color6',
};

/**
 * Parse a color string into RGBA, Gradient, or ColorRef format
 *
 * Supported formats:
 * - Named colors: primary, secondary, tertiary, text, accent, background
 * - Hex: #fff, #ffffff, #ffffffff (with alpha)
 * - RGBA: rgba(255, 255, 255, 1)
 * - RGB: rgb(255, 255, 255)
 *
 * @param value - Color string to parse
 * @returns Parsed color, color reference, or undefined if invalid/empty
 */
export const parseColor = (value: string | null | undefined): ParsedColor | undefined => {
  if (!value || value.trim() === '') {
    return undefined;
  }

  const trimmed = value.trim().toLowerCase();

  // Handle named colors (global style references)
  const colorRef = COLOR_NAME_MAP[trimmed];
  if (colorRef) {
    return colorRef;
  }

  // Handle hex colors
  if (trimmed.startsWith('#')) {
    return hexToRGBA(trimmed);
  }

  // Handle rgba()
  if (trimmed.startsWith('rgba(')) {
    return parseRGBAString(trimmed);
  }

  // Handle rgb()
  if (trimmed.startsWith('rgb(')) {
    return parseRGBString(trimmed);
  }

  // Handle gradient() - future extension
  if (trimmed.startsWith('gradient(')) {
    return parseGradientString(trimmed);
  }

  return undefined;
};

/**
 * Convert hex color to RGBA
 */
const hexToRGBA = (hex: string): RGBA | undefined => {
  // Remove # prefix
  let hexValue = hex.slice(1);

  // Expand shorthand (e.g., #fff → #ffffff)
  if (hexValue.length === 3) {
    hexValue = hexValue
      .split('')
      .map((c) => c + c)
      .join('');
  }

  // Handle 4-char shorthand with alpha (e.g., #ffff)
  if (hexValue.length === 4) {
    hexValue = hexValue
      .split('')
      .map((c) => c + c)
      .join('');
  }

  // Parse 6-char hex (no alpha)
  if (hexValue.length === 6) {
    const r = parseInt(hexValue.slice(0, 2), 16);
    const g = parseInt(hexValue.slice(2, 4), 16);
    const b = parseInt(hexValue.slice(4, 6), 16);

    if (isNaN(r) || isNaN(g) || isNaN(b)) {
      return undefined;
    }

    return { r, g, b, a: 1 };
  }

  // Parse 8-char hex (with alpha)
  if (hexValue.length === 8) {
    const r = parseInt(hexValue.slice(0, 2), 16);
    const g = parseInt(hexValue.slice(2, 4), 16);
    const b = parseInt(hexValue.slice(4, 6), 16);
    const a = parseInt(hexValue.slice(6, 8), 16) / 255;

    if (isNaN(r) || isNaN(g) || isNaN(b) || isNaN(a)) {
      return undefined;
    }

    return { r, g, b, a: Math.round(a * 100) / 100 };
  }

  return undefined;
};

/**
 * Parse rgba(r, g, b, a) string
 */
const parseRGBAString = (rgba: string): RGBA | undefined => {
  const match = rgba.match(/rgba\(\s*(\d+)\s*,\s*(\d+)\s*,\s*(\d+)\s*,\s*([\d.]+)\s*\)/);
  if (!match) {
    return undefined;
  }

  const r = parseInt(match[1], 10);
  const g = parseInt(match[2], 10);
  const b = parseInt(match[3], 10);
  const a = parseFloat(match[4]);

  if (isNaN(r) || isNaN(g) || isNaN(b) || isNaN(a)) {
    return undefined;
  }

  return {
    r: Math.min(255, Math.max(0, r)),
    g: Math.min(255, Math.max(0, g)),
    b: Math.min(255, Math.max(0, b)),
    a: Math.min(1, Math.max(0, a)),
  };
};

/**
 * Parse rgb(r, g, b) string
 */
const parseRGBString = (rgb: string): RGBA | undefined => {
  const match = rgb.match(/rgb\(\s*(\d+)\s*,\s*(\d+)\s*,\s*(\d+)\s*\)/);
  if (!match) {
    return undefined;
  }

  const r = parseInt(match[1], 10);
  const g = parseInt(match[2], 10);
  const b = parseInt(match[3], 10);

  if (isNaN(r) || isNaN(g) || isNaN(b)) {
    return undefined;
  }

  return {
    r: Math.min(255, Math.max(0, r)),
    g: Math.min(255, Math.max(0, g)),
    b: Math.min(255, Math.max(0, b)),
    a: 1,
  };
};

/**
 * Split gradient stops string by commas, respecting parentheses
 * This allows rgba() colors to be used in gradient stops
 *
 * @example
 * "rgba(255,0,0,0.5):0, rgba(0,0,255,0.5):100" → ["rgba(255,0,0,0.5):0", "rgba(0,0,255,0.5):100"]
 * "#ff0000:0, #0000ff:100" → ["#ff0000:0", "#0000ff:100"]
 */
const splitGradientStops = (stopsString: string): string[] => {
  const stops: string[] = [];
  let current = '';
  let parenDepth = 0;

  for (const char of stopsString) {
    if (char === '(') {
      parenDepth++;
      current += char;
    } else if (char === ')') {
      parenDepth--;
      current += char;
    } else if (char === ',' && parenDepth === 0) {
      // Only split on commas outside parentheses
      if (current.trim()) {
        stops.push(current.trim());
      }
      current = '';
    } else {
      current += char;
    }
  }

  // Don't forget the last stop
  if (current.trim()) {
    stops.push(current.trim());
  }

  return stops;
};

/**
 * Parse gradient string
 * Format: gradient(linear|radial, angle, color:position, color:position, ...)
 *
 * @example
 * gradient(linear, 90, #ff0000:0, #0000ff:100)
 * gradient(linear, 135, rgba(96,116,136,0.5):0, rgba(139,154,171,0.5):100)
 * gradient(radial, 0, #ffffff:0, #000000:100)
 */
const parseGradientString = (gradient: string): Gradient | undefined => {
  // Match: gradient(type, angle, ...stops)
  const match = gradient.match(/gradient\(\s*(linear|radial)\s*,\s*(\d+)\s*,\s*(.+)\s*\)/);
  if (!match) {
    return undefined;
  }

  const type = match[1] as 'linear' | 'radial';
  const angle = parseInt(match[2], 10);
  const stopsString = match[3];

  // Split stops respecting parentheses (for rgba colors)
  const stopStrings = splitGradientStops(stopsString);

  const stops = stopStrings.map((stop) => {
    // Find the last colon which separates color from position
    // This handles rgba(r,g,b,a):position format
    const lastColonIndex = stop.lastIndexOf(':');
    if (lastColonIndex === -1) {
      return { hexa: stop.trim(), position: 0 };
    }

    const color = stop.slice(0, lastColonIndex).trim();
    const position = parseInt(stop.slice(lastColonIndex + 1).trim() || '0', 10);

    return {
      hexa: color || '#000000',
      // Convert percentage (0-100) to fractional (0-1) for getGradientString
      position: isNaN(position) ? 0 : position / 100,
    };
  });

  if (stops.length < 2) {
    return undefined;
  }

  return {
    type: type === 'linear' ? 'Linear' : 'Radial',
    angle,
    stops,
  };
};
